import React, { useMemo, useCallback, useEffect, useState, useContext } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import DDSideNavbar from './components/DDSideNavbar';
import { ChevronLeftIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  buildDDNavSections,
  getActionableOtherConditions,
  getDDRouteForSectionIndex,
  getDDStepRoutes,
  hasActionableOtherConditions,
  isAdhdPositiveDecision,
  isOtherConditionsComplete,
  getFlatOtherConditionsStepTitle,
  getFinalFormulationStepTitle,
} from '../../../utils/diagnosticDecisionRouting';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { TriggerSembleMdtReport } from '../../../utils/TriggerSembleMdtReport';
import ModuleCompletionDialog, {
  CancelButton,
  PrimaryActionButton,
  GenerateReportButton,
  SummarySection,
  DialogMessage,
} from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { serverTimestamp, doc, runTransaction } from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  buildFirstGeneratedFieldsDotNotation,
  buildFirstCompletedFieldsDotNotation,
  buildFirstCompletedFields,
  appendReportAuditEntry,
} from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import useEmergingFormulations from '../../../hooks/useEmergingFormulations';
import DualClinicianEmergingNotes from './components/DualClinicianEmergingNotes';
import { resolvePadStatusOnDdComplete } from '../../../utils/padUnlockUtils';
import { isPadStaleVsDd } from '../../../utils/conditionChecks';
import PadRedoDialog from '../../../components/PadRedoDialog';
import DictationTextArea from '../../../components/DictationTextArea';
import { ReadOnlyAutoResizeTextarea } from '../../../components/AutoResizeTextarea';

/**
 * 3_Formulation.js — Final Formulation page for multi-step DD (TOC, SLaM,
 * WLC V2).
 *
 * This is the last step in the Diagnostic Decision module for TOC / SLaM.
 * On WLC V2 with an ADHD-positive decision, MDT Plan follows this page.
 * It displays:
 *
 *  1. Decision for ADHD summary (read-only, from sections['1'])
 *  2. Decision for Other Conditions summary (read-only, from
 *     sections['2'].finalComorbidDiagnoses — only if applicable)
 *  3. Emerging Formulation (read-only) — SLaM shows SSI + Lead side by side
 *     so the Lead can copy from either into the final box; TOC shows the
 *     single assessing-clinician notes from CI section 12
 *  4. Final Formulation text area (editable, saved to sections['3'].finalFormulation)
 *
 * The clinician writes their final formulation here (potentially copy-pasting
 * from the emerging formulation), which will later be fed into the LLM to
 * produce a report paragraph for review.
 *
 * The module-completion transaction (DD status = completed, PAD unlock,
 * report flags) fires from this page when the clinician clicks Complete —
 * except on WLC V2 ADHD-positive, where completion runs from MDT Plan.
 * In that case this page must still kick off Formulation paragraph
 * generation before navigating, because the completion dialog (which
 * normally calls GenerateDraftWithLLM for "Formulation") is skipped.
 *
 * Patients without multi-step DD are redirected away.
 */

// Module-level badge — must NOT be defined inside Formulation's render body.
// An inline component definition remounts the ADHD / Other Conditions summary
// panels on every keystroke in Final Formulation, which re-runs read-only
// textarea height reflow and jumps the page scroll position.
const DECISION_BADGE_STYLES = {
  'Fully met':       'bg-green-100 text-green-800 border-green-200',
  'Partially met':   'bg-amber-100 text-amber-800 border-amber-200',
  'Not met':         'bg-red-100   text-red-700   border-red-200',
  'Confirm diagnosis': 'bg-green-100 text-green-800 border-green-200',
  'Unable to confirm': 'bg-amber-100 text-amber-800 border-amber-200',
  'Provide next steps': 'bg-blue-100 text-blue-800 border-blue-200',
  'Rule out':          'bg-red-100   text-red-700   border-red-200',
};

function DecisionBadge({ decision }) {
  const style = DECISION_BADGE_STYLES[decision] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${style}`}>
      {decision}
    </span>
  );
}

export default function Formulation() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } =
    useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);
  const canEdit = useCanEditStage(patientDataModel, 'DD');

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // SLaM PAD redo decision (v2). When the finalised Diagnostic Decision disagrees
  // with the outcome the Post-Assessment Discussion was already completed under,
  // we surface the Redo vs "Leave as is" choice here at DD completion. We remember
  // which post-completion navigation the clinician originally chose so we can
  // resume it after they pick "Leave as is".
  const [showRedoDecision, setShowRedoDecision] = useState(false);
  const [pendingNavChoice, setPendingNavChoice] = useState(null);

  // Final formulation text authored by the clinician on this page.
  // Persisted to dataModel.4_diagnosticDecision.sections.3.finalFormulation.
  // Lazy initializer reads once on mount; local state is never overwritten by
  // subsequent patientDataModel refreshes (avoids the race where a Firestore
  // write-refresh cycle clobbers text the user has typed but not yet blurred).
  const [finalFormulation, setFinalFormulation] = useState(() => {
    return patientDataModel?.dataModel?.['4_diagnosticDecision']
      ?.sections?.['3']?.finalFormulation || '';
  });

  // ---------------------------------------------------------------------------
  // Route guard: Final Formulation exists only on multi-step DD workflows
  // (TOC + SLaM / 3 Borough). Standard one-/two-page DD orgs are redirected.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (patientDataModel && !wf.hasMultiStepDD) {
      navigate(`/patient/${patientId}/proforma/diagnostic-decision/adhd`, { replace: true });
    }
  }, [patientDataModel, wf.hasMultiStepDD, patientId, navigate]);

  // ---------------------------------------------------------------------------
  // Section exit regeneration — fires LLM paragraph generation on unmount if
  // the clinician has changed the formulation since last generation.
  // ---------------------------------------------------------------------------
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    patientDataModel,
    stageTitle: 'Diagnostic Decision',
    sectionTitle: 'Formulation',
    hasChanges,
    canEdit,
  });

  // ---------------------------------------------------------------------------
  // Read-only data: ADHD decision summary
  // ---------------------------------------------------------------------------
  const adhdDecision = useMemo(() => {
    const s1 = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'] || {};
    return {
      diagnosisDecision: s1.diagnosisDecision || null,
      diagnosisPresentation: s1.diagnosisPresentation || null,
      diagnosisSeverity: s1.diagnosisSeverity || null,
      partialDiagnosisClassification: s1.partialDiagnosisClassification || null,
      otherSpecifiedReason: s1.otherSpecifiedReason || null,
      otherSpecifiedReasonText: s1.otherSpecifiedReasonText || null,
      supportedByCollateral: s1.supportedByCollateral || [],
      clinicianNotes: s1.clinicianNotes || '',
    };
  }, [patientDataModel]);

  // Build an Oxford-comma list from an array of strings.
  // e.g. ['A', 'B', 'C'] → "A, B, and C"
  const toOxfordList = (items) => {
    if (!items?.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  };

  // Build an array of { label, value } pairs summarising the ADHD decision.
  // The first entry (the decision itself) is shown as a badge, so bullets
  // here only cover the supporting detail rows (index 1+).
  const adhdSummaryBullets = useMemo(() => {
    const d = adhdDecision;
    if (!d.diagnosisDecision) return [];

    const bullets = [];

    if (d.diagnosisDecision === 'Fully met') {
      if (d.diagnosisPresentation) bullets.push({ label: 'Presentation', value: d.diagnosisPresentation });
      if (d.diagnosisSeverity)     bullets.push({ label: 'Severity',     value: d.diagnosisSeverity });
    } else if (d.diagnosisDecision === 'Partially met') {
      if (d.partialDiagnosisClassification) {
        bullets.push({ label: 'Classification', value: d.partialDiagnosisClassification });
        if (d.partialDiagnosisClassification === 'Other Specified ADHD' && d.otherSpecifiedReason) {
          const reasonValue = d.otherSpecifiedReason === 'Other' && d.otherSpecifiedReasonText?.trim()
            ? `Other — ${d.otherSpecifiedReasonText}`
            : d.otherSpecifiedReason;
          bullets.push({ label: 'Reason', value: reasonValue });
        }
      }
    } else if (d.diagnosisDecision === 'Not met' && d.clinicianNotes) {
      bullets.push({ label: 'Next Steps', value: d.clinicianNotes });
    }

    if (['Fully met', 'Partially met'].includes(d.diagnosisDecision) &&
        d.supportedByCollateral?.length > 0) {
      bullets.push({ label: 'Supporting Evidence', value: toOxfordList(d.supportedByCollateral) });
    }

    return bullets;
  }, [adhdDecision]);

  // ---------------------------------------------------------------------------
  // Read-only data: Other Conditions decisions
  // ---------------------------------------------------------------------------
  // hasOtherConditions drives routing/progress/completion — overridden to false
  // when skipDifferentialsDecisions is true (TOC), removing the "Other
  // Conditions" step from the sidebar and navigation entirely.
  const hasOtherConditions = useMemo(
    () => hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.skipDifferentialsDecisions],
  );

  // Raw emerging differentials list — always computed regardless of skip flag.
  // When skipDifferentialsDecisions is true and there are no historical
  // decisions, this is displayed as a read-only reference list so clinicians
  // can reference the emerging differentials when writing the formulation.
  const emergingDifferentials = useMemo(
    () => getActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
    }),
    [patientDataModel, wf.isFlatDifferentials],
  );

  // Backstop for the sidebar lock: even though the Final Formulation sidebar item
  // is locked until every other condition is decided, a clinician could still land
  // here via a direct URL / browser history. This guarantees the module cannot be
  // completed with any differential left undecided (which would otherwise silently
  // drop it from the report or produce a "determination not made" paragraph).
  // When skipDifferentialsDecisions is true, otherConditionsComplete is not
  // required because no per-condition decisions are expected.
  const otherConditionsComplete = useMemo(
    () => isOtherConditionsComplete({ patientDataModel, isFlatDifferentials: wf.isFlatDifferentials }),
    [patientDataModel, wf.isFlatDifferentials],
  );

  const otherConditionDecisions = useMemo(() => {
    const fcd = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['2']?.finalComorbidDiagnoses || {};
    return Object.entries(fcd)
      .map(([name, data]) => ({
        name,
        decision: data?.decision || 'No decision',
        notes: data?.notes || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [patientDataModel]);

  // Detect historical decisions from section 2 — these exist for patients
  // whose DD was completed BEFORE differentials decisions were disabled. When
  // present, we preserve them as a read-only panel for historical record-keeping.
  const hasHistoricalDecisions = otherConditionDecisions.length > 0;

  // ---------------------------------------------------------------------------
  // Read-only Emerging Formulation reference(s)
  // SLaM: both SSI + Lead columns from emergingFormulations for easy copy/paste.
  // TOC: single assessing-clinician notes (CI section 12 via hook fallback).
  // Confidential notes are intentionally omitted here — they belong on Review,
  // not in the final formulation drafting step.
  // ---------------------------------------------------------------------------
  const emerging = useEmergingFormulations(patientDataModel);

  const ssiColleagueNotes = emerging.ssiClinician;
  const leadColleagueNotes = emerging.leadClinician;

  const ssiDisplayName =
    ssiColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianSSI?.clinicianName
    || 'SSI clinician';
  const leadDisplayName =
    leadColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianLead?.clinicianName
    || 'Lead clinician';

  const ssiLastUpdated = useMemo(() => {
    if (ssiColleagueNotes?.updatedAt) return ssiColleagueNotes.updatedAt;
    const reportTs = patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated;
    if (reportTs) return reportTs;
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']?.firstCompletedAt || null;
  }, [patientDataModel, ssiColleagueNotes?.updatedAt]);

  const leadLastUpdated = useMemo(() => {
    if (leadColleagueNotes?.updatedAt) return leadColleagueNotes.updatedAt;
    return patientDataModel?.dataModel?.['2_clinicalInterview']?.firstCompletedAt || null;
  }, [patientDataModel, leadColleagueNotes?.updatedAt]);

  // TOC / single-clinician fallback text
  const singleEmergingFormulation =
    leadColleagueNotes?.formulation
    || patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]
      ?.clinicianNotes?.['Additional Notes']
    || '';

  // ---------------------------------------------------------------------------
  // Firestore writes for the Final Formulation text
  // ---------------------------------------------------------------------------
  // Queues the current finalFormulation to Firestore. Called by the blur
  // handler, navigation helpers, and the unmount safety-net so that the
  // formulation is persisted regardless of how the clinician leaves the page.
  const persistFormulation = useCallback(() => {
    if (!canEdit) return;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '3': {
            sectionTitle: 'Formulation',
            finalFormulation,
          },
        },
      },
    });
  }, [canEdit, patientId, finalFormulation, updatePatientFirestore]);

  // onBlur handler — persists to Firestore but intentionally does NOT set
  // hasChanges (dirty-tracking lives exclusively in onChange). Skips the
  // write when the value matches Firestore to avoid unnecessary writes from
  // DictationTextArea's unmount safety-net blur.
  const handleFormulationBlur = useCallback(() => {
    if (!canEdit) return;

    const storedValue = patientDataModel?.dataModel?.['4_diagnosticDecision']
      ?.sections?.['3']?.finalFormulation || '';
    if (finalFormulation === storedValue) return;

    try {
      persistFormulation();
    } catch (error) {
      console.error('Error persisting final formulation:', error);
    }
  }, [canEdit, finalFormulation, patientDataModel, persistFormulation]);

  // ---------------------------------------------------------------------------
  // DDSideNavbar — currentSectionIndex depends on whether "Other Conditions"
  // is applicable.  If it is, Formulation is index 3; otherwise index 2.
  // ---------------------------------------------------------------------------
  const formulationSectionIndex = hasOtherConditions ? 3 : 2;

  // SLaM / 3 Borough use a longer displayed title so clinicians know this box
  // is for triangulation across sources (informant, SSI, CI), not just emerging notes.
  const finalFormulationTitle = getFinalFormulationStepTitle(wf.isSLaMWorkflow);

  const ddNavSections = useMemo(
    () => buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
      currentSectionIndex: formulationSectionIndex,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, wf.isSembleMdtReportEnabled, wf.skipDifferentialsDecisions, formulationSectionIndex],
  );

  // Whether MDT Plan appears in the sidebar for this patient. Must match the
  // condition used in buildDDNavSections so sidebar click indices stay aligned.
  const ddSection1 = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = wf.isSembleMdtReportEnabled && isAdhdPositiveDecision(ddSection1);

  // Index→route must omit /other-conditions when that step isn't in the
  // sidebar (see getDDRouteForSectionIndex). formulationSectionIndex already
  // accounts for this for the active highlight; navigation must match.
  const navigateToDDSection = useCallback(async (index) => {
    persistFormulation();
    flushPendingUpdates();
    navigate(getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan));
  }, [patientId, navigate, persistFormulation, flushPendingUpdates, hasOtherConditions, hasMdtPlan]);

  // ---------------------------------------------------------------------------
  // Module completion
  // ---------------------------------------------------------------------------
  const shouldShowAdhdFlow = true; // TOC is always ADHD flow

  // "Go to Post-Assessment Discussion" at DD completion.
  // SLaM: never show it. PAD is independent of DD in every finish path —
  // Path A (PAD in appointment) already completed PAD before DD; Path B
  // (defer PAD) books PAD later after DD. The only forward CTA is Generate
  // Report → Documents Dashboard.
  const shouldShowPADButton = useCallback(() => {
    if (wf.isSLaMWorkflow) return false;
    return shouldShowAdhdFlow || wf.isSSIAlwaysExpected;
  }, [shouldShowAdhdFlow, wf.isSSIAlwaysExpected, wf.isSLaMWorkflow]);

  // Polish the clinician's Final Formulation notes into dd_finalFormulation.
  // TOC / SLaM fire this from the completion-dialog "Generate Report" path.
  // WLC V2 ADHD-positive skips that dialog and goes to MDT Plan, so the
  // same call must run on that hop — RD_DD only shows paragraphs that have
  // generated content, so a missing trigger looks like "Final Formulation
  // never appears". First visit (!isGenerated) and later edits (hasChanges)
  // both need a run; an empty skip matches the processor, which ignores
  // blank notes.
  const triggerFormulationGeneration = useCallback((dataReady) => {
    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Formulation');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(
        patientId,
        'Diagnostic Decision',
        'Formulation',
        [],
        dataReady,
      );
    }
  }, [patientDataModel, hasChanges, patientId]);

  const handleFinishFormulation = useCallback(async () => {
    if (!canEdit) return;
    // Backstop guard: never complete the module while any other condition is
    // still undecided (mirrors the disabled button + the sidebar lock).
    // Skipped when differentials decisions are disabled for this workflow.
    if (!wf.skipDifferentialsDecisions && hasOtherConditions && !otherConditionsComplete) return;
    await flushPendingUpdates();
    setIsSaving(true);

    try {
      // Save section 3 status as completed
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            '3': {
              sectionTitle: 'Formulation',
              finalFormulation,
              status: 'completed',
              progress: 100,
            },
          },
        },
      });

      await fetchPatientData(patientId);
      setIsSaving(false);

      // When the MDT Plan step exists, Formulation is not the terminal step --
      // navigate to MDT Plan instead of opening the completion dialog. The
      // module completion transaction runs from 4_MDTPlan.js. Generation
      // still belongs here: notes are committed, and MDT Plan never requests
      // the Formulation section. markRegenHandled stops the unmount hook
      // from firing a second time after we navigate.
      if (hasMdtPlan) {
        triggerFormulationGeneration(Promise.resolve());
        markRegenHandled();
        navigate(getDDStepRoutes(patientId).mdtPlan);
        return;
      }

      setIsDialogOpen(true);
    } catch (error) {
      console.error('Failed to save formulation:', error);
      setIsSaving(false);
    }
  }, [canEdit, wf.skipDifferentialsDecisions, hasOtherConditions, otherConditionsComplete, patientId, finalFormulation, fetchPatientData, updatePatientFirestore, flushPendingUpdates, hasMdtPlan, navigate, markRegenHandled, triggerFormulationGeneration]);

  const handleNavigationChoice = async (choice) => {
    if (!canEdit) return;
    markRegenHandled();
    setIsNavigating(true);

    const dataReady = flushPendingUpdates();
    triggerFormulationGeneration(dataReady);

    try {
      await dataReady;

      // Module completion — marks DD as complete, unlocks PAD, sets report flags
      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const ddFirstFields = buildFirstGeneratedFieldsDotNotation('dd', manageReports);
      const ddFirstCompletedFields = buildFirstCompletedFieldsDotNotation(
        '4_diagnosticDecision',
        patientDataModel?.dataModel?.['4_diagnosticDecision'],
      );

      // Only unlock PAD when still locked — Path 1 may have already opened PAD.
      const padStatusUpdate = resolvePadStatusOnDdComplete(patientDataModel, true);

      try {
        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const updates = {
            'dataModel.4_diagnosticDecision.status': 'completed',
            'dataModel.4_diagnosticDecision.progress': 100,
            'dataModel.4_diagnosticDecision.docs_available': true,
            'dataModel.4_diagnosticDecision.lastCompletedAt': serverTimestamp(),
            'dataModel.4_diagnosticDecision.lastCompletedBy': auth.currentUser.uid,
            'dataModel.4_diagnosticDecision.lastCompletedByName': auth.currentUser.displayName,
            ...ddFirstCompletedFields,
            'dataModel.6_manageReports.ddReportGenerated': true,
            'dataModel.6_manageReports.ddReportGeneratedAt': serverTimestamp(),
            'dataModel.6_manageReports.ddReportGeneratedBy': auth.currentUser.uid,
            'dataModel.6_manageReports.ddReportGeneratedByName': auth.currentUser.displayName,
            ...ddFirstFields,
          };
          if (padStatusUpdate) {
            updates['dataModel.5_postAssessmentDiscussion.status'] = padStatusUpdate;
          }
          transaction.update(docRef, updates);
        });
      } catch (transactionError) {
        console.warn('Direct transaction failed, falling back to updatePatientFirestore:', transactionError);
        // Include firstCompletedFields so a rare transaction failure still stamps
        // firstCompletedAt (and keeps the inferred-appointment audit accurate).
        const fallback = {
          '4_diagnosticDecision': {
            status: 'completed',
            progress: 100,
            docs_available: true,
            lastCompletedAt: serverTimestamp(),
            lastCompletedBy: auth.currentUser.uid,
            lastCompletedByName: auth.currentUser.displayName,
            ...buildFirstCompletedFields(patientDataModel?.dataModel?.['4_diagnosticDecision']),
          },
          '6_manageReports': {
            ddReportGenerated: true,
            ddReportGeneratedAt: serverTimestamp(),
            ddReportGeneratedBy: auth.currentUser.uid,
            ddReportGeneratedByName: auth.currentUser.displayName,
          },
        };
        if (padStatusUpdate) {
          fallback['5_postAssessmentDiscussion'] = { status: padStatusUpdate };
        }
        await updatePatientFirestore(patientId, fallback);
      }

      appendReportAuditEntry(patientId, {
        module: 'dd',
        eventType: 'generated',
        isFirstOccurrence: !manageReports?.ddReportFirstGeneratedAt,
      });

      // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates).
      // Called once after transaction or fallback succeeds — not on early nav to Formulation.
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'dd',
        didWriteFirstCompleted: Object.keys(ddFirstCompletedFields).length > 0,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // Semble MDT Report orgs: this completion path is reached for non-positive
      // ADHD outcomes (ADHD-positive patients complete on MDT Plan instead).
      // Fire paragraph generation now that DD is complete so the partial report
      // (Sections 1-2, no Plan) is ready for review on the Documents page.
      if (wf.isSembleMdtReportEnabled) {
        TriggerSembleMdtReport({ clinicianId: auth.currentUser.uid, patientId });
      }

      await fetchPatientData(patientId);

      // SLaM PAD redo decision point. The Diagnostic Decision has just been
      // finalised in the transaction above, so if the Post-Assessment Discussion
      // was already completed under an outcome that now disagrees with that
      // decision, offer the clinician the Redo vs "Leave as is" choice once here,
      // before we move on. We evaluate staleness against a model that reflects the
      // just-completed DD, because the in-closure patientDataModel predates the
      // refetch above (React state updates are not visible inside this closure).
      const modelWithDdComplete = {
        ...patientDataModel,
        dataModel: {
          ...patientDataModel?.dataModel,
          '4_diagnosticDecision': {
            ...patientDataModel?.dataModel?.['4_diagnosticDecision'],
            status: 'completed',
          },
        },
      };
      if (wf.isSLaMWorkflow && isPadStaleVsDd(modelWithDdComplete)) {
        setPendingNavChoice(choice);
        setIsDialogOpen(false);
        setIsNavigating(false);
        setShowRedoDecision(true);
        return;
      }

      proceedAfterCompletion(choice);
    } catch (error) {
      console.error('Error completing Diagnostic Decision module:', error);
      setIsNavigating(false);
    }
  };

  // Performs the post-completion navigation (kick off report generation, or go
  // straight to the Post-Assessment Discussion). Extracted so it can run either
  // immediately (no mismatch) or after the clinician resolves the redo decision
  // by choosing "Leave as is".
  const proceedAfterCompletion = (choice) => {
    if (choice === 'documents') {
      setIsDialogOpen(false);
      setIsGenerating(true);
    } else if (choice === 'pad') {
      setIsDialogOpen(false);
      navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
    }
  };

  // "Leave it as is": record the acknowledgement so the persistent staleness cue
  // drops to its quieter (non-alarming) state, then continue with whichever
  // navigation the clinician originally chose in the completion dialog.
  const handleLeaveMismatchAsIs = async () => {
    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        padMismatchAcknowledgedAt: serverTimestamp(),
        padMismatchAcknowledgedBy: auth.currentUser?.uid || null,
        padMismatchAcknowledgedByName: auth.currentUser?.displayName || null,
      },
    });
    await flushPendingUpdates();
    setShowRedoDecision(false);
    proceedAfterCompletion(pendingNavChoice || 'documents');
  };

  // After a successful redo the fresh Post-Assessment Discussion is open for the
  // new outcome; refetch and land the clinician on its Review page to begin it.
  const handleRedoComplete = async () => {
    await fetchPatientData(patientId, true);
    setShowRedoDecision(false);
    navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
  };

  const handleGenerateReport = () => handleNavigationChoice('documents');
  const handleGoToPostAssessmentDiscussion = () => handleNavigationChoice('pad');

  // ---------------------------------------------------------------------------
  // Form validity — require a non-empty final formulation AND, when other
  // conditions exist, that every one of them has been decided. The latter is a
  // backstop for the sidebar lock (a clinician could reach this page directly).
  // ---------------------------------------------------------------------------
  const isFormValid = useCallback(() => {
    const hasFormulationText = Boolean(finalFormulation && finalFormulation.trim().length > 0);
    // When skipDifferentialsDecisions is true, otherConditionsComplete is not
    // required — the Other Conditions step does not exist in the workflow.
    if (wf.skipDifferentialsDecisions) return hasFormulationText;
    return hasFormulationText && (!hasOtherConditions || otherConditionsComplete);
  }, [finalFormulation, hasOtherConditions, otherConditionsComplete, wf.skipDifferentialsDecisions]);

  // Explains why the Complete button is disabled specifically because of
  // undecided other conditions (distinct from a missing formulation), surfaced
  // as a tooltip so the clinician knows to go back and finish the decisions.
  // When differentials decisions are disabled, the Other Conditions step does
  // not exist and cannot block completion.
  const otherConditionsBlockMessage =
    !wf.skipDifferentialsDecisions && hasOtherConditions && !otherConditionsComplete
      ? `Complete "${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}" — every condition needs a decision — before completing the Diagnostic Decision.`
      : '';

  // ---------------------------------------------------------------------------
  // Breadcrumbs
  // ---------------------------------------------------------------------------
  const breadcrumbTuples = useMemo(() => [
    ['Patient Overview', `/patient/${patientId}`],
    ['Diagnostic Decision', `/patient/${patientId}/proforma/diagnostic-decision/mdt-review`],
    ['Formulation', null],
  ], [patientId]);


  if (!patientDataModel || !wf.hasMultiStepDD) return null;

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      <div className="flex items-start">
        <DDSideNavbar
          sections={ddNavSections}
          currentSectionIndex={formulationSectionIndex}
          navigateToSection={navigateToDDSection}
          isNavigating={isNavigating}
        />

        <div className="flex-grow p-8">
          {/* Top navigation bar: back (left) + page title (centre) + forward (right) */}
          <div className="flex justify-between items-center mb-6">
            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => {
                  persistFormulation();
                  flushPendingUpdates();
                  const backRoute = hasOtherConditions
                    ? `/patient/${patientId}/proforma/diagnostic-decision/other-conditions`
                    : `/patient/${patientId}/proforma/diagnostic-decision/adhd`;
                  navigate(backRoute);
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">
                  {hasOtherConditions
                    ? `Back to ${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}`
                    : 'Back to ADHD Decision'}
                </span>
              </button>
            </div>

            <div className="w-1/3 flex justify-center">
              <h1 className="text-2xl font-bold">{finalFormulationTitle}</h1>
            </div>

            <div className="w-1/3 flex justify-end">
              {canEdit && (
                <button
                  onClick={handleFinishFormulation}
                  title={otherConditionsBlockMessage || undefined}
                  className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                    !isFormValid() || isSaving
                      ? isSaving
                        ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                  }`}
                  disabled={!isFormValid() || isSaving}
                >
                  <span className="font-bold">
                    {isSaving ? 'Saving...' : 'Complete Diagnostic Decision'}
                  </span>
                  {isSaving
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )}
            </div>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Panels 1 + 2: ADHD decision and Other Conditions / Emerging    */}
          {/* Differentials side-by-side. Panel 2 has three variants:        */}
          {/*   - Standard: decision panel (non-skip orgs with conditions)   */}
          {/*   - Historical: preserved decisions from before the change     */}
          {/*   - Read-only: emerging differentials list (skip orgs, no      */}
          {/*     historical decisions)                                      */}
          {/* ---------------------------------------------------------------- */}
          {(() => {
            // Determine whether to show a second panel at all
            const showStandardPanel2 = hasOtherConditions;
            const showHistoricalPanel2 = wf.skipDifferentialsDecisions && hasHistoricalDecisions;
            const showEmergingPanel2 = wf.skipDifferentialsDecisions && !hasHistoricalDecisions && emergingDifferentials.length > 0;
            const showPanel2 = showStandardPanel2 || showHistoricalPanel2 || showEmergingPanel2;
            return (
              <div className={`grid gap-6 mb-6 ${showPanel2 ? 'grid-cols-2' : 'grid-cols-1'}`}>

                {/* Panel 1: Decision for ADHD */}
                <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-left mb-4">Decision for ADHD</h3>

                  {/* Condition subheading — mirrors the condition name in the Other Conditions panel */}
                  <p className="text-left text-sm font-semibold text-gray-800 mb-1.5">Attention Deficit Hyperactivity Disorder (ADHD)</p>

                  {/* Primary decision badge — left-aligned */}
                  {adhdDecision.diagnosisDecision ? (
                    <>
                      <div className="flex justify-start mb-3">
                        <DecisionBadge decision={adhdDecision.diagnosisDecision} />
                      </div>

                      {/* Detail rows (Presentation, Severity, etc.) in a read-only styled
                          block. A <div> is used instead of <textarea> so bold labels
                          can be preserved — it is styled identically to the gray
                          read-only textareas used for clinician notes. */}
                      {adhdSummaryBullets.length > 0 && (
                        <div className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 opacity-75 mb-3 space-y-1.5 text-left">
                          {adhdSummaryBullets.map(({ label, value }, i) => (
                            <p key={i} className="leading-snug">
                              <span className="font-semibold">{label}:</span> {value}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Clinician notes — read-only textarea, styled like non-editable fields.
                         Skipped for "Not met" because the notes are already displayed as a
                         "Next Steps" bullet in the summary block above. */}
                      {adhdDecision.clinicianNotes && adhdDecision.diagnosisDecision !== 'Not met' && (
                        <ReadOnlyAutoResizeTextarea
                          value={adhdDecision.clinicianNotes}
                          className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 resize-none cursor-not-allowed opacity-75 text-left"
                          ariaLabel="ADHD clinician notes"
                        />
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No decision recorded yet.</p>
                  )}
                </div>

                {/* Panel 2a: Standard — Decision for Other Conditions (non-skip orgs) */}
                {showStandardPanel2 && (
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-left mb-4">
                      {getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}
                    </h3>
                    {otherConditionDecisions.length === 0 ? (
                      <p className="text-sm text-gray-400 italic text-left">No decisions have been recorded yet.</p>
                    ) : (
                      <div className="space-y-5">
                        {otherConditionDecisions.map((cond) => (
                          <div key={cond.name} className="text-left">
                            {/* Condition name */}
                            <p className="text-sm font-semibold text-gray-800 mb-1.5">{cond.name}</p>

                            {/* Decision badge */}
                            <div className="mb-2">
                              <DecisionBadge decision={cond.decision} />
                            </div>

                            {/* Clinician notes — read-only textarea, styled like non-editable fields */}
                            {cond.notes && (
                              <ReadOnlyAutoResizeTextarea
                                value={cond.notes}
                                className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 resize-none cursor-not-allowed opacity-75 text-left"
                                ariaLabel={`Notes for ${cond.name}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Panel 2b: Historical — Preserved decisions from before differentials
                    decisions were disabled (historical record-keeping). Shown only when
                    skipDifferentialsDecisions is true and the patient has section 2
                    decisions from a prior DD completion. */}
                {showHistoricalPanel2 && (
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-left mb-4">
                      Decision on Differentials (Historical)
                    </h3>
                    <div className="space-y-5">
                      {otherConditionDecisions.map((cond) => (
                        <div key={cond.name} className="text-left">
                          <p className="text-sm font-semibold text-gray-800 mb-1.5">{cond.name}</p>
                          <div className="mb-2">
                            <DecisionBadge decision={cond.decision} />
                          </div>
                          {cond.notes && (
                            <ReadOnlyAutoResizeTextarea
                              value={cond.notes}
                              className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-700 resize-none cursor-not-allowed opacity-75 text-left"
                              ariaLabel={`Notes for ${cond.name}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Panel 2c: Read-only emerging differentials — shown when differentials
                    decisions are disabled and there are no historical decisions. Lists the
                    conditions selected during earlier assessment stages so clinicians can
                    reference them when writing the formulation. */}
                {showEmergingPanel2 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 pt-5 pb-2">
                    <h3 className="text-lg font-semibold text-left mb-4">
                      Emerging Differentials
                    </h3>
                    <p className="text-sm text-gray-500 mb-3 text-left">
                      The following conditions were identified as emerging differentials during earlier assessment stages. You may reference these when writing the formulation below.
                    </p>
                    <ul className="list-disc list-inside space-y-1.5 text-left">
                      {emergingDifferentials.map((condition) => (
                        <li key={condition} className="text-sm text-gray-800">{condition}</li>
                      ))}
                    </ul>
                    <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
                      Not included in final report
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ---------------------------------------------------------------- */}
          {/* Panel 3: Emerging Formulation (read-only reference)             */}
          {/* SLaM: SSI + Lead side by side for copy/paste into Final below.  */}
          {/* TOC: single assessing-clinician panel (grey, historic style).   */}
          {/* ---------------------------------------------------------------- */}
          <DualClinicianEmergingNotes
            showDual={wf.hasCIReview}
            ssiClinician={ssiColleagueNotes}
            leadClinician={leadColleagueNotes}
            ssiDisplayName={ssiDisplayName}
            leadDisplayName={leadDisplayName}
            ssiLastUpdated={ssiLastUpdated}
            leadLastUpdated={leadLastUpdated}
            showConfidential={false}
            singleFormulation={singleEmergingFormulation}
            singleLastUpdated={ssiLastUpdated}
            singleVariant="gray"
          />

          {/* ---------------------------------------------------------------- */}
          {/* Panel 4: Final Formulation (editable)                           */}
          {/* Visually distinguished from read-only panels with a white       */}
          {/* background and an active border to signal this is where the     */}
          {/* clinician writes their notes.                                   */}
          {/* ---------------------------------------------------------------- */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-left mb-1">{finalFormulationTitle}</h3>
            <p className="text-xs text-gray-400 mb-3 text-left">
              {wf.hasCIReview
                ? 'Record your final diagnostic formulation below. You can copy and paste from either Emerging Formulation box above.'
                : 'Record your final diagnostic formulation below. You can copy and paste content from the Emerging Formulation above.'}
            </p>
            <DictationTextArea
              value={finalFormulation}
              onChange={(value) => {
                setFinalFormulation(value);
                setHasChanges(true);
              }}
              onBlur={handleFormulationBlur}
              canEdit={canEdit}
              placeholder="Enter the final formulation here..."
              ariaLabel="Final formulation"
            />
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Bottom navigation buttons — mirrors top bar for long-page convenience */}
          {/* ---------------------------------------------------------------- */}
          <div className="flex justify-between items-center mb-8">
            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => {
                  persistFormulation();
                  flushPendingUpdates();
                  const backRoute = hasOtherConditions
                    ? `/patient/${patientId}/proforma/diagnostic-decision/other-conditions`
                    : `/patient/${patientId}/proforma/diagnostic-decision/adhd`;
                  navigate(backRoute);
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">
                  {hasOtherConditions
                    ? `Back to ${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}`
                    : 'Back to ADHD Decision'}
                </span>
              </button>
            </div>

            <div className="w-1/3" />

            <div className="w-1/3 flex justify-end">
              {canEdit && (
                <button
                  onClick={handleFinishFormulation}
                  title={otherConditionsBlockMessage || undefined}
                  className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                    !isFormValid() || isSaving
                      ? isSaving
                        ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                  }`}
                  disabled={!isFormValid() || isSaving}
                >
                  <span className="font-bold">
                    {isSaving ? 'Saving...' : 'Complete Diagnostic Decision'}
                  </span>
                  {isSaving
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Module Completion Dialog */}
      {/* -------------------------------------------------------------------- */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Diagnostic Decision Complete"
        description="You have completed the Diagnostic Decision module including the final formulation."
        actions={(() => {
          // When PAD is hidden (SLaM), Generate Report is the sole forward
          // action and must render as the primary CTA — not a secondary one
          // that used to sit beside "Go to PAD".
          const showPad = shouldShowPADButton();
          const generateReportBtn = (
            <GenerateReportButton
              key="generate"
              onClick={() => { if (!isNavigating) handleGenerateReport(); }}
              disabled={isNavigating}
              secondary={showPad && !wf.isMDTReview}
              savingLabel={isNavigating ? 'Saving...' : undefined}
            />
          );

          const padBtn = showPad && (
            <PrimaryActionButton
              key="pad"
              label={isNavigating ? 'Saving...' : 'Go to Post-Assessment Discussion'}
              onClick={() => { if (!isNavigating) handleGoToPostAssessmentDiscussion(); }}
              disabled={isNavigating}
              secondary={wf.isMDTReview}
            />
          );

          return (
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              {wf.isMDTReview
                ? <>{padBtn}{generateReportBtn}</>
                : <>{generateReportBtn}{padBtn}</>}
            </>
          );
        })()}
      >
        {/* Decision recap — mirrors the Autism DD dialog (4_AsdDDFormulation.js).
            Shows the ADHD outcome, any per-condition decisions (non-TOC orgs that
            still have differentials decisions enabled), and the read-only emerging
            differentials list (TOC / skip-differentials flow). */}
        {adhdDecision.diagnosisDecision && (
          <SummarySection title="Decision for ADHD:">
            <p className="text-sm text-gray-700">
              Attention Deficit Hyperactivity Disorder (ADHD) — <span className="font-semibold">{adhdDecision.diagnosisDecision}</span>
            </p>
            {adhdSummaryBullets.length > 0 && (
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5 mt-1">
                {adhdSummaryBullets.map((b) => (
                  <li key={b.label}>{b.label}: <span className="font-semibold">{b.value}</span></li>
                ))}
              </ul>
            )}
          </SummarySection>
        )}
        {!wf.skipDifferentialsDecisions && otherConditionDecisions.length > 0 && (
          <SummarySection title="Decisions for Other Conditions:">
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {otherConditionDecisions.map((cond) => (
                <li key={cond.name}>
                  {cond.name}: <span className="font-semibold">{cond.decision}</span>
                </li>
              ))}
            </ul>
          </SummarySection>
        )}
        <DialogMessage>Are you happy to proceed?</DialogMessage>
      </ModuleCompletionDialog>

      {/* Generate Report progress overlay */}
      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={() => navigate(`/patient/${patientId}/documents`)}
        isGenerating={isGenerating}
        onCancel={() => setIsGenerating(false)}
      />

      {/* SLaM PAD redo decision — shown at DD completion when the finalised
          decision disagrees with the outcome the Post-Assessment Discussion was
          completed under. Lets the clinician redo the discussion for the new
          outcome or knowingly leave the original in place. */}
      <PadRedoDialog
        isOpen={showRedoDecision}
        onClose={() => setShowRedoDecision(false)}
        patientId={patientId}
        patientDataModel={patientDataModel}
        mode="decision"
        diagnosisDecision={adhdDecision.diagnosisDecision}
        onLeaveAsIs={handleLeaveMismatchAsIs}
        onBeforeRedo={flushPendingUpdates}
        onAfterRedo={handleRedoComplete}
      />
    </Layout>
  );
}
